// src/pages/PrivacyPolicy.js
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6 transition"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Privacy Policy
          </h1>
          <p className="text-slate-600">
            Last Updated: November 6, 2025
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 md:p-12 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              1. Introduction
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              SkillSyncs ("we," "our," or "us") is committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform, website, mobile applications, and related services (collectively, the "Service").
            </p>
            <p className="text-slate-700 leading-relaxed">
              By using our Service, you agree to the collection and use of information in accordance with this Privacy Policy. If you do not agree with our policies and practices, please do not use our Service.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              2. Information We Collect
            </h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  2.1 Information You Provide to Us
                </h3>
                <p className="text-slate-700 leading-relaxed mb-3">
                  We collect information that you provide directly to us when you:
                </p>
                <ul className="list-disc list-inside ml-4 space-y-2 text-slate-700">
                  <li><strong>Account Registration:</strong> Name, email address, password, role (client or helper), and profile information</li>
                  <li><strong>Profile Information:</strong> Bio, skills, experience level, location (city, country, continent, timezone), availability, portfolio links, and other professional details</li>
                  <li><strong>Problem Submissions:</strong> Problem descriptions, technical details, files, tags, urgency level, and related information</li>
                  <li><strong>Communications:</strong> Messages, updates, notes, and other content you share through the Service</li>
                  <li><strong>Support Requests:</strong> Information you provide when contacting our support team</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  2.2 Automatically Collected Information
                </h3>
                <p className="text-slate-700 leading-relaxed mb-3">
                  When you use our Service, we automatically collect certain information, including:
                </p>
                <ul className="list-disc list-inside ml-4 space-y-2 text-slate-700">
                  <li><strong>Usage Data:</strong> Information about how you interact with the Service, including pages visited, features used, and time spent</li>
                  <li><strong>Device Information:</strong> Device type, operating system, browser type, IP address, and unique device identifiers</li>
                  <li><strong>Log Data:</strong> Server logs, including timestamps, access times, and error information</li>
                  <li><strong>Location Data:</strong> General location information based on your IP address or timezone settings</li>
                  <li><strong>Cookies and Tracking Technologies:</strong> Information collected through cookies, web beacons, and similar tracking technologies</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  2.3 Information from Third Parties
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  We may receive information about you from third-party services, such as authentication providers and analytics services, in accordance with their privacy policies and our agreements with them.
                </p>
              </div>
            </div>
          </section>

          {/* How We Use Information */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              3. How We Use Your Information
            </h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              We use the information we collect for various purposes, including:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-2 text-slate-700">
              <li><strong>Service Provision:</strong> To provide, maintain, and improve our Service, including matching clients with helpers, facilitating communications, and managing workspaces</li>
              <li><strong>Account Management:</strong> To create and manage your account, verify your identity, and authenticate your access to the Service</li>
              <li><strong>Communication:</strong> To send you service-related notifications, updates, and support communications</li>
              <li><strong>Personalization:</strong> To personalize your experience, recommend relevant helpers or problems, and customize content</li>
              <li><strong>Analytics and Improvement:</strong> To analyze usage patterns, improve our algorithms, and enhance the Service's functionality</li>
              <li><strong>Security:</strong> To detect, prevent, and address fraud, security issues, and technical problems</li>
              <li><strong>Legal Compliance:</strong> To comply with legal obligations, enforce our Terms of Service, and protect our rights and the rights of our users</li>
              <li><strong>Marketing:</strong> To send you promotional communications (with your consent, where required by law) about features, updates, and special offers</li>
            </ul>
          </section>

          {/* Information Sharing */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              4. How We Share Your Information
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  4.1 With Other Users
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  When you use our Service, certain information may be visible to other users:
                </p>
                <ul className="list-disc list-inside ml-4 space-y-2 text-slate-700 mt-2">
                  <li><strong>Public Profiles:</strong> Your name, bio, skills, experience level, and other profile information may be visible to other users</li>
                  <li><strong>Problem Information:</strong> Problem details you submit may be visible to helpers for matching purposes</li>
                  <li><strong>Workspace Communications:</strong> Messages and files shared within a workspace are visible to the Customer and Expert involved</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  4.2 With Service Providers
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  We may share your information with third-party service providers who perform services on our behalf, including:
                </p>
                <ul className="list-disc list-inside ml-4 space-y-2 text-slate-700 mt-2">
                  <li>Cloud hosting and storage providers (e.g., Firebase, Google Cloud)</li>
                  <li>Email delivery services</li>
                  <li>Analytics and monitoring services</li>
                  <li>Customer support platforms</li>
                </ul>
                <p className="text-slate-700 leading-relaxed mt-3">
                  These service providers are contractually obligated to protect your information and use it only for the purposes we specify.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  4.3 Legal Requirements
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  We may disclose your information if required by law, regulation, legal process, or governmental request, or to protect our rights, property, or safety, or that of our users or others.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  4.4 Business Transfers
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  In the event of a merger, acquisition, reorganization, or sale of assets, your information may be transferred to the acquiring entity, subject to the same privacy protections.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  4.5 With Your Consent
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  We may share your information with third parties when you explicitly consent to such sharing.
                </p>
              </div>
            </div>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              5. Data Security
            </h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              We implement appropriate technical and organizational security measures to protect your information against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-2 text-slate-700">
              <li>Encryption of data in transit and at rest</li>
              <li>Secure authentication and access controls</li>
              <li>Regular security assessments and updates</li>
              <li>Firewall and intrusion detection systems</li>
              <li>Employee training on data protection</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              6. Data Retention
            </h2>
            <p className="text-slate-700 leading-relaxed">
              We retain your information for as long as necessary to provide the Service, fulfill the purposes described in this Privacy Policy, comply with legal obligations, resolve disputes, and enforce our agreements. When you delete your account, we will delete or anonymize your personal information, except where we are required to retain it for legal, regulatory, or legitimate business purposes.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              7. Your Privacy Rights
            </h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              Depending on your location, you may have certain rights regarding your personal information, including:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-2 text-slate-700">
              <li><strong>Access:</strong> The right to request access to your personal information</li>
              <li><strong>Correction:</strong> The right to request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> The right to request deletion of your personal information</li>
              <li><strong>Portability:</strong> The right to request a copy of your data in a portable format</li>
              <li><strong>Objection:</strong> The right to object to certain processing activities</li>
              <li><strong>Restriction:</strong> The right to request restriction of processing</li>
              <li><strong>Withdrawal of Consent:</strong> The right to withdraw consent where processing is based on consent</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              To exercise these rights, please contact us at skillsyncs@outlook.com. We will respond to your request within a reasonable timeframe and in accordance with applicable law.
            </p>
          </section>

          {/* Cookies and Tracking */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              8. Cookies and Tracking Technologies
            </h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              We use cookies and similar tracking technologies to collect and store information about your preferences and activity on the Service. Cookies are small data files stored on your device that help us:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-2 text-slate-700">
              <li>Remember your preferences and settings</li>
              <li>Authenticate your account</li>
              <li>Analyze usage patterns and improve the Service</li>
              <li>Provide personalized content and features</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              We use essential cookies (required for authentication and security) and optional functional cookies (for enhanced user experience). We do <strong>not</strong> use advertising or analytics cookies.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              For detailed information about the specific cookies we use, their purposes, retention periods, and how to manage your cookie preferences, please see our{" "}
              <a href="/cookie-policy.html" className="text-blue-600 underline hover:text-blue-700">
                Cookie Policy
              </a>.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              You can control cookies through your browser settings or by using our cookie preferences manager (accessible via the "Cookie Preferences" link in the website footer). However, disabling essential cookies may limit your ability to use certain features of the Service.
            </p>
          </section>

          {/* International Data Transfers */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              9. International Data Transfers
            </h2>
            <p className="text-slate-700 leading-relaxed">
              SkillSyncs operates globally, and your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your country. By using our Service, you consent to the transfer of your information to these countries. We take appropriate measures to ensure that your information receives adequate protection in accordance with this Privacy Policy and applicable data protection laws.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              10. Children's Privacy
            </h2>
            <p className="text-slate-700 leading-relaxed">
              Our Service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected personal information from a child without parental consent, we will take steps to delete such information. If you believe we have collected information from a child, please contact us immediately.
            </p>
          </section>

          {/* Third-Party Links */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              11. Third-Party Links and Services
            </h2>
            <p className="text-slate-700 leading-relaxed">
              Our Service may contain links to third-party websites, services, or applications that are not operated by us. We are not responsible for the privacy practices of these third parties. We encourage you to review the privacy policies of any third-party services you access through our Service.
            </p>
          </section>

          {/* Changes to Privacy Policy */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              12. Changes to This Privacy Policy
            </h2>
            <p className="text-slate-700 leading-relaxed">
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of material changes by posting the updated Privacy Policy on our website and updating the "Last Updated" date. We may also notify you via email or through the Service. Your continued use of the Service after such changes constitutes your acceptance of the updated Privacy Policy.
            </p>
          </section>

          {/* California Privacy Rights */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              13. California Privacy Rights (CCPA)
            </h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA), including:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-2 text-slate-700">
              <li>The right to know what personal information we collect, use, disclose, and sell</li>
              <li>The right to request deletion of your personal information</li>
              <li>The right to opt-out of the sale of your personal information (we do not sell personal information)</li>
              <li>The right to non-discrimination for exercising your privacy rights</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              To exercise these rights, please contact us at skillsyncs@outlook.com.
            </p>
          </section>

          {/* GDPR Rights */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              14. European Privacy Rights (GDPR)
            </h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              If you are located in the European Economic Area (EEA), you have additional rights under the General Data Protection Regulation (GDPR), including:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-2 text-slate-700">
              <li>The right to access your personal data</li>
              <li>The right to rectification of inaccurate data</li>
              <li>The right to erasure ("right to be forgotten")</li>
              <li>The right to restrict processing</li>
              <li>The right to data portability</li>
              <li>The right to object to processing</li>
              <li>Rights related to automated decision-making and profiling</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              To exercise these rights, please contact us at skillsyncs@outlook.com. You also have the right to lodge a complaint with your local data protection authority.
            </p>
          </section>

          {/* Contact */}
          <section className="pt-8 border-t border-slate-200">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              15. Contact Us
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="text-slate-700">
              <p><strong>Email:</strong> skillsyncs@outlook.com</p>
              <p className="mt-2"><strong>Address:</strong> SkillSyncs Global Services</p>
              <p className="mt-4 text-sm text-slate-600">
                We are committed to addressing your privacy concerns and will respond to your inquiries within a reasonable timeframe.
              </p>
            </div>
          </section>
        </div>

        {/* Footer Navigation */}
        <div className="mt-8 flex justify-between items-center">
          <Link
            to="/"
            className="text-blue-600 hover:text-blue-700 transition"
          >
            ← Back to Home
          </Link>
          <Link
            to="/terms"
            className="text-blue-600 hover:text-blue-700 transition"
          >
            Terms of Service →
          </Link>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;

